// Local Library UI preferences (Phase 1 redesign).
//
// Held separately from libraryStore.ts and libraryCacheStore.ts so we can
// persist a tight subset (collectorMode) without round-tripping the entire
// album cache through localStorage on every UI flick.
//
// SCOPE:
//   - viewMode      → which "mission control" view is active
//   - collectorMode → power-user toggle; persists per device
//   - activeFilters → in-memory filter state, set by FilterChips OR by
//                     sidebar collection-preset clicks (Hi-Res, DSD, …)
//
// Cross-device sync (Supabase) is intentionally NOT wired in Phase 1 —
// localStorage matches the existing pattern for `theme` and other per-device
// preferences. Flagged in TECH_DEBT for the upcoming user-prefs sync pass.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type LibraryViewMode =
  | 'album_wall'
  | 'collector'
  | 'technical'
  | 'folder'
  | 'metadata_studio';

export type ArtworkFilterMode = 'any' | 'has_artwork' | 'missing_artwork';
export type RecentlyAddedWindow = '' | '7d' | '30d' | '90d';

export interface LibraryActiveFilters {
  // Format codes mirror the existing libraryService format param: FLAC, DSF,
  // WAV, AIFF, MP3, M4A. 'DSD' is normalized → 'DSF' for the API; the badge
  // logic treats DSD64/128/256/512 as a single "DSD" family.
  format: string;
  sampleRate: string;       // '44100' | '48000' | '88200' | '96000' | … | 'DSD64' | …
  bitDepth: string;         // '16' | '24' | '32' | '' (DSD bypasses)
  sourceId: string;         // matches a user_library_sources.id
  artwork: ArtworkFilterMode;
  recentlyAdded: RecentlyAddedWindow;
  // Sidebar "collection preset" — a soft, named filter that the FilterChips
  // can render as a removable chip. Examples: 'hires', 'dsd', 'needs_artwork'.
  // Empty string = none.
  preset: string;
  presetLabel: string;
}

const EMPTY_FILTERS: LibraryActiveFilters = {
  format: '',
  sampleRate: '',
  bitDepth: '',
  sourceId: '',
  artwork: 'any',
  recentlyAdded: '',
  preset: '',
  presetLabel: '',
};

export type InspectorTargetType = 'album' | 'track' | 'artist';
export interface InspectorTarget {
  type: InspectorTargetType;
  id: string;
  album?: any;
}

export type SortDir = 'asc' | 'desc';
export interface SortState {
  field: string;
  dir: SortDir;
}

export type AlbumWallSort =
  | 'title_asc'
  | 'title_desc'
  | 'artist_asc'
  | 'artist_desc'
  | 'added_desc'
  | 'added_asc';

interface LibraryUIState {
  viewMode: LibraryViewMode;
  collectorMode: boolean;
  activeFilters: LibraryActiveFilters;

  inspectorOpen: boolean;
  inspectorTarget: InspectorTarget | null;

  collectorSort: SortState | null;
  technicalSort: SortState | null;
  albumWallSort: AlbumWallSort;
  selectedAlbumIds: string[];

  setViewMode: (mode: LibraryViewMode) => void;
  setCollectorMode: (on: boolean) => void;
  toggleCollectorMode: () => void;

  openInspector: (target: InspectorTarget) => void;
  closeInspector: () => void;
  setInspectorTarget: (target: InspectorTarget) => void;

  setCollectorSort: (sort: SortState | null) => void;
  setTechnicalSort: (sort: SortState | null) => void;
  setAlbumWallSort: (sort: AlbumWallSort) => void;
  setSelectedAlbumIds: (ids: string[]) => void;
  toggleAlbumSelection: (id: string) => void;
  clearAlbumSelection: () => void;

  setFilter: <K extends keyof LibraryActiveFilters>(key: K, value: LibraryActiveFilters[K]) => void;
  clearFilter: (key: keyof LibraryActiveFilters) => void;
  clearAllFilters: () => void;
  applyPreset: (preset: string, label: string, partial: Partial<LibraryActiveFilters>) => void;

  hasAnyFilter: () => boolean;
}

const STORAGE_KEY = 'maestro:libraryUI';

export const useLibraryUIStore = create<LibraryUIState>()(
  persist(
    (set, get) => ({
      viewMode: 'album_wall',
      collectorMode: false,
      activeFilters: EMPTY_FILTERS,
      inspectorOpen: false,
      inspectorTarget: null,
      collectorSort: null,
      technicalSort: null,
      albumWallSort: 'added_desc',
      selectedAlbumIds: [],

      setViewMode: (viewMode) => set({ viewMode, selectedAlbumIds: [] }),
      setCollectorMode: (on) => set({ collectorMode: !!on }),
      toggleCollectorMode: () => set((s) => ({ collectorMode: !s.collectorMode })),

      openInspector: (target) => set({ inspectorOpen: true, inspectorTarget: target }),
      closeInspector: () => set({ inspectorOpen: false }),
      setInspectorTarget: (target) => set({ inspectorTarget: target }),

      setCollectorSort: (sort) => set({ collectorSort: sort }),
      setTechnicalSort: (sort) => set({ technicalSort: sort }),
      setAlbumWallSort: (albumWallSort) => set({ albumWallSort }),
      setSelectedAlbumIds: (ids) => set({ selectedAlbumIds: ids }),
      toggleAlbumSelection: (id) => set((s) => {
        const set_ = new Set(s.selectedAlbumIds);
        if (set_.has(id)) set_.delete(id); else set_.add(id);
        return { selectedAlbumIds: [...set_] };
      }),
      clearAlbumSelection: () => set({ selectedAlbumIds: [] }),

      setFilter: (key, value) =>
        set((s) => ({ activeFilters: { ...s.activeFilters, [key]: value } })),

      clearFilter: (key) =>
        set((s) => {
          const next = { ...s.activeFilters };
          if (key === 'artwork') next.artwork = 'any';
          else if (key === 'recentlyAdded') next.recentlyAdded = '';
          else if (key === 'preset' || key === 'presetLabel') {
            next.preset = '';
            next.presetLabel = '';
          } else (next as any)[key] = '';
          return { activeFilters: next };
        }),

      clearAllFilters: () => set({ activeFilters: EMPTY_FILTERS }),

      applyPreset: (preset, label, partial) =>
        set({
          activeFilters: {
            ...EMPTY_FILTERS,
            ...partial,
            preset,
            presetLabel: label,
          },
        }),

      hasAnyFilter: () => {
        const f = get().activeFilters;
        return !!(
          f.format ||
          f.sampleRate ||
          f.bitDepth ||
          f.sourceId ||
          (f.artwork && f.artwork !== 'any') ||
          f.recentlyAdded ||
          f.preset
        );
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Persist ONLY the prefs that should survive reloads. activeFilters is
      // intentionally session-scoped — a sidebar preset click should not
      // silently re-apply hours later when the user opens the app again.
      partialize: (state) => ({
        viewMode: state.viewMode,
        collectorMode: state.collectorMode,
        albumWallSort: state.albumWallSort,
      }),
      version: 1,
    },
  ),
);

// Convenient selectors so consumers can subscribe to a single primitive and
// avoid the v5 "object literal returns a new ref every render" footgun.
export const useViewMode = () => useLibraryUIStore((s) => s.viewMode);
export const useCollectorMode = () => useLibraryUIStore((s) => s.collectorMode);
export const useActiveFilters = () => useLibraryUIStore((s) => s.activeFilters);
export const useInspectorOpen = () => useLibraryUIStore((s) => s.inspectorOpen);
export const useInspectorTarget = () => useLibraryUIStore((s) => s.inspectorTarget);
export const useCollectorSort = () => useLibraryUIStore((s) => s.collectorSort);
export const useTechnicalSort = () => useLibraryUIStore((s) => s.technicalSort);
export const useSelectedAlbumIds = () => useLibraryUIStore((s) => s.selectedAlbumIds);
export const useAlbumWallSort = () => useLibraryUIStore((s) => s.albumWallSort);
